import {
  deleteLocalEodReport,
  listLocalEodReports,
  submitLocalEodReport,
} from './utils/eodLocalStore';

const BASE = process.env.REACT_APP_API_URL || '/api';

const EOD_PATHS = ['/settings/eod', '/eod', '/calls/eod'];

/** Session cache of endpoints known missing on this server (avoids 404 spam). */
const unavailable = new Set();

function isNotFoundError(err) {
  const msg = (err?.message || '').toLowerCase();
  return err?.status === 404
    || msg.includes('not found')
    || msg.includes('cast to objectid');
}

function isEodFallbackError(err) {
  return isNotFoundError(err)
    || err?.status === 502
    || err?.status === 503
    || (err?.message || '').toLowerCase().includes('failed to fetch')
    || (err?.message || '').toLowerCase().includes('network');
}

function markUnavailable(key) {
  unavailable.add(key);
}

function isUnavailable(key) {
  return unavailable.has(key);
}

async function req(path, opts = {}) {
  const url = BASE + path;
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const config = {
    headers: { ...headers, ...(opts.headers || {}) },
    ...opts,
  };
  if (opts.body && typeof opts.body !== 'string' && !(opts.body instanceof FormData)) {
    config.body = JSON.stringify(opts.body);
  } else if (opts.body) {
    config.body = opts.body;
  }

  const res = await fetch(url, config);
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText || 'API Error' }));
    if (res.status === 401 && !url.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    const error = new Error(errBody.error || res.statusText || 'API Error');
    error.status = res.status;
    error.body = errBody; // keeps structured fields (e.g. `duplicate`) available to callers
    throw error;
  }
  return res.json();
}

async function reqWithFallback(paths, opts = {}) {
  let lastErr;
  for (const path of paths) {
    const key = path.split('?')[0];
    if (isUnavailable(key)) continue;
    try {
      return await req(path, opts);
    } catch (e) {
      lastErr = e;
      if (isNotFoundError(e) || e.status === 404) {
        markUnavailable(key);
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error('Not Found');
}

function markEodApiUnavailable() {
  EOD_PATHS.forEach(markUnavailable);
}

function shouldUseLocalEod(err) {
  return isEodFallbackError(err);
}

function eodApiSkipped() {
  return EOD_PATHS.every(isUnavailable);
}

async function withLocalEodFallback(fn, localFn) {
  if (eodApiSkipped()) return localFn();
  try {
    return await fn();
  } catch (e) {
    if (!shouldUseLocalEod(e)) throw e;
    markEodApiUnavailable();
    return localFn();
  }
}

async function uploadReq(path, formData) {
  const url = BASE + path;
  const token = localStorage.getItem('token');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { method: 'POST', headers, body: formData });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Upload Error');
  }
  return res.json();
}

async function tryBootstrap(path, params) {
  if (isUnavailable('/bootstrap')) {
    const err = new Error('Not Found');
    err.status = 404;
    throw err;
  }
  try {
    const qs = params ? '?' + new URLSearchParams(params) : '';
    return await req(path + qs);
  } catch (e) {
    if (isNotFoundError(e) || e.status === 404) markUnavailable('/bootstrap');
    throw e;
  }
}

async function tryProposals(paths, opts, emptyOnMiss = false) {
  if (isUnavailable('/proposals')) {
    if (emptyOnMiss) return [];
    const err = new Error('Proposals API is not available on this server yet');
    err.status = 404;
    throw err;
  }
  try {
    const data = await reqWithFallback(paths, opts);
    return data;
  } catch (e) {
    if (isNotFoundError(e) || e.status === 404) {
      markUnavailable('/proposals');
      markUnavailable('/settings/proposals');
      if (emptyOnMiss) return [];
    }
    throw e;
  }
}

const api = {
  // Auth
  login: (data) => req('/auth/login', { method: 'POST', body: data }),
  getLoginUsers: () => req('/auth/login-users'),
  getMe: () => req('/auth/me'),
  getUsers: () => req('/auth'),
  getTeamHistory: () => req('/auth/team-history'),
  createUser: (data) => req('/auth/register', { method: 'POST', body: data }),
  updateUser: (id, data) => req('/auth/' + id, { method: 'PUT', body: data }),
  resetUserPassword: (id, newPassword) => req('/auth/' + id + '/reset-password', { method: 'PUT', body: { newPassword } }),
  deleteUser: (id, body) => req('/auth/' + id, { method: 'DELETE', body: body || {} }),
  getUserTransferPreview: (id) => req('/auth/' + id + '/transfer-preview'),

  // Tasks (personal action pad — per user)
  getTasks: () => req('/tasks'),
  createTask: (data) => req('/tasks', { method: 'POST', body: data }),
  updateTask: (id, data) => req('/tasks/' + id, { method: 'PUT', body: data }),
  deleteTask: (id) => req('/tasks/' + id, { method: 'DELETE' }),

  // Queue
  generateQueue: (data) => req('/queue/generate', { method: 'POST', body: data }),
  selfGenerateQueue: (data) => req('/queue/self-generate', { method: 'POST', body: data }),
  getQueue: (params) => req('/queue?' + new URLSearchParams(params || {})),
  manualQueue: (data) => req('/queue/manual', { method: 'POST', body: data }),
  updateQueueStatus: (spocId, data) => req('/queue/' + spocId + '/status', { method: 'POST', body: data }),

  // Bootstrap (batched initial load) — skipped after first 404 on this session
  getBootstrap: () => tryBootstrap('/bootstrap'),
  getBootstrapActivity: (params) => tryBootstrap('/bootstrap/activity', params),
  isBootstrapAvailable: () => !isUnavailable('/bootstrap'),

  // Companies
  getCompanies: (params) => req('/companies?' + new URLSearchParams(params || {})),
  getCompany: (id) => req('/companies/' + id),
  createCompany: (data) => req('/companies', { method: 'POST', body: data }),
  updateCompany: (id, data) => req('/companies/' + id, { method: 'PUT', body: data }),
  toggleTopDeveloperCompany: (id, stars) => req('/companies/' + id, { method: 'PUT', body: { isTopDeveloper: stars > 0, topStars: stars } }),
  deleteCompany: (id) => req('/companies/' + id, { method: 'DELETE' }),
  concludeDeal: (id, data) => req('/companies/' + id + '/conclude', { method: 'POST', body: data }),
  reactivateCompany: (id) => req('/companies/' + id + '/reactivate', { method: 'POST' }),
  requestDrop: (id, data) => req('/companies/' + id + '/request-drop', { method: 'POST', body: data }),
  approveDrop: (id) => req('/companies/' + id + '/approve-drop', { method: 'POST' }),
  rejectDrop: (id) => req('/companies/' + id + '/reject-drop', { method: 'POST' }),
  getPendingDrops: () => req('/companies/status/pending-drops'),
  getDropped: () => req('/companies/status/dropped'),
  recycleCheck: () => req('/companies/recycle/check', { method: 'POST' }),
  reconcilePreview: () => req('/companies/reconcile-preview'),
  reconcileLeads: (merges) => req('/companies/reconcile-leads', { method: 'POST', body: { merges } }),
  reconcileDiagnostic: (city) => req('/companies/reconcile-diagnostic?' + new URLSearchParams(city ? { city } : {})),
  // Prefer client-side scan on production until /find-duplicates is deployed
  findDuplicateCompanies: (city) => {
    if (isUnavailable('/companies/find-duplicates')) {
      const err = new Error('Not Found');
      err.status = 404;
      throw err;
    }
    return req('/companies/find-duplicates?' + new URLSearchParams(city ? { city } : {})).catch((e) => {
      if (isNotFoundError(e) || e.status === 404 || e.status === 500) {
        markUnavailable('/companies/find-duplicates');
      }
      throw e;
    });
  },
  mergeDuplicateCompanies: (data) => req('/companies/merge-duplicates', { method: 'POST', body: data }),
  checkCompanyBeforeCreate: (name, city) => req('/companies/check-before-create', { method: 'POST', body: { name, city } }),
  uploadCompanies: (formData) => uploadReq('/companies/upload', formData),
  uploadCompaniesWithSpocs: (formData) => uploadReq('/companies/upload-with-spocs', formData),

  // SPOCs
  getSpocs: (params) => req('/spocs?' + new URLSearchParams(params || {})),
  getSpoc: (id) => req('/spocs/' + id),
  getSpocTrail: (id) => req('/spocs/' + id + '/trail'),
  checkSpocPhone: (phone, excludeId) => req('/spocs/check-phone?' + new URLSearchParams({ phone, ...(excludeId ? { excludeId } : {}) })),
  createSpoc: (data) => req('/spocs', { method: 'POST', body: data }),
  updateSpoc: (id, data) => req('/spocs/' + id, { method: 'PUT', body: data }),
  deleteSpoc: (id) => req('/spocs/' + id, { method: 'DELETE' }),
  toggleHot: (id) => req('/spocs/' + id + '/toggle-hot', { method: 'POST' }),
  strikeoutSpoc: (id, reason) => req('/spocs/' + id + '/strikeout', { method: 'POST', body: { reason } }),
  restoreSpoc: (id) => req('/spocs/' + id + '/strikeout', { method: 'POST', body: { restore: true } }),

  // Calls
  getCalls: (params) => req('/calls?' + new URLSearchParams(params || {})),
  getCallStats: (params) => req('/calls/stats/summary?' + new URLSearchParams(params || {})),
  createCall: (data) => req('/calls', { method: 'POST', body: data }),
  updateCall: (id, data) => req('/calls/' + id, { method: 'PUT', body: data }),
  deleteCall: (id) => req('/calls/' + id, { method: 'DELETE' }),

  // Meetings
  getMeetings: (params) => req('/meetings?' + new URLSearchParams(params || {})),
  createMeeting: (data) => req('/meetings', { method: 'POST', body: data }),
  updateMeeting: (id, data) => req('/meetings/' + id, { method: 'PUT', body: data }),
  deleteMeeting: (id) => req('/meetings/' + id, { method: 'DELETE' }),

  // Proposals — silent empty list when API missing (production often lacks this route)
  getProposals: (params) => {
    const qs = '?' + new URLSearchParams(params || {});
    return tryProposals([`/settings/proposals${qs}`, `/proposals${qs}`, `/companies/proposals${qs}`], {}, true);
  },
  createProposal: (data) => tryProposals(['/settings/proposals', '/proposals', '/companies/proposals'], { method: 'POST', body: data }),
  updateProposal: (id, data) => tryProposals([`/settings/proposals/${id}`, `/proposals/${id}`, `/companies/proposals/${id}`], { method: 'PUT', body: data }),
  deleteProposal: (id) => tryProposals([`/settings/proposals/${id}`, `/proposals/${id}`, `/companies/proposals/${id}`], { method: 'DELETE' }),
  isProposalsAvailable: () => !isUnavailable('/proposals'),

  // Lead Bank
  getLeadBank: (params) => req('/leadbank?' + new URLSearchParams(params || {})),
  toggleTopDeveloperLead: (id, stars) => req('/leadbank/' + id, { method: 'PUT', body: { isTopDeveloper: stars > 0, topStars: stars } }),
  bulkAddLeads: (data) => req('/leadbank/bulk', { method: 'POST', body: data }),
  bulkTopDevelopers: (data) => req('/leadbank/bulk-top', { method: 'POST', body: data }),
  updateLead: (id, data) => req('/leadbank/' + id, { method: 'PUT', body: data }),
  markLeadDone: (id, data) => req('/leadbank/' + id + '/done', { method: 'POST', body: data || {} }),
  deleteLead: (id) => req('/leadbank/' + id, { method: 'DELETE' }),
  cleanupJunkLeads: () => req('/leadbank/cleanup-junk', { method: 'POST' }),
  uploadLeads: (formData) => uploadReq('/leadbank/upload', formData),

  // SOPs
  getSops: (params) => req('/sops?' + new URLSearchParams(params || {})),
  createSop: (data) => req('/sops', { method: 'POST', body: data }),
  updateSop: (id, data) => req('/sops/' + id, { method: 'PUT', body: data }),
  deleteSop: (id) => req('/sops/' + id, { method: 'DELETE' }),

  // Settings
  getSettings: () => req('/settings'),
  updateSettings: (data) => req('/settings', { method: 'PUT', body: data }),
  updateDiscordSettings: (data) => req('/settings/discord', { method: 'PUT', body: data }),

  // Analytics
  getSourceAnalytics: (params) => req('/analytics/sources?' + new URLSearchParams(params || {})),
  getPersonAnalytics: (params) => req('/analytics/persons?' + new URLSearchParams(params || {})),
  getFunnel: (params) => req('/analytics/funnel?' + new URLSearchParams(params || {})),
  getDailyCalls: (days, params) => req('/analytics/daily-calls?' + new URLSearchParams({ days: days || 30, ...(params || {}) })),
  getPipelineValue: (params) => req('/analytics/pipeline-value?' + new URLSearchParams(params || {})),
  getCityAnalytics: (params) => req('/analytics/cities?' + new URLSearchParams(params || {})),
  getConversionRates: (params) => req('/analytics/conversion-rates?' + new URLSearchParams(params || {})),

  // Visits
  getVisits: () => req('/visits'),
  getVisit: (id) => req('/visits/' + id),
  createVisit: (data) => req('/visits', { method: 'POST', body: data }),
  updateVisit: (id, data) => req('/visits/' + id, { method: 'PUT', body: data }),
  deleteVisit: (id) => req('/visits/' + id, { method: 'DELETE' }),

  // Targets
  getTargets: (params) => req('/targets?' + new URLSearchParams(params || {})),
  createTarget: (data) => req('/targets', { method: 'POST', body: data }),
  deleteTarget: (id) => req('/targets/' + id, { method: 'DELETE' }),
  getTargetActuals: (params) => req('/targets/actuals?' + new URLSearchParams(params || {})),
  getTargetActualsBatch: (params) => req('/targets/actuals/batch?' + new URLSearchParams(params || {})),

  // EOD Reports
  getEodPreview: (params) => {
    const qs = '?' + new URLSearchParams(params || {});
    return reqWithFallback([
      `/settings/eod/preview${qs}`,
      `/eod/preview${qs}`,
      `/calls/eod/preview${qs}`,
    ]).catch(err => {
      if (isEodFallbackError(err)) markEodApiUnavailable();
      throw err;
    });
  },
  submitEod: (data) =>
    withLocalEodFallback(
      () => reqWithFallback(EOD_PATHS, { method: 'POST', body: data }),
      () => submitLocalEodReport(data)
    ),
  getEodReports: (params) => {
    const qs = '?' + new URLSearchParams(params || {});
    return withLocalEodFallback(
      () => reqWithFallback([`/settings/eod${qs}`, `/eod${qs}`, `/calls/eod${qs}`]),
      () => listLocalEodReports(params || {})
    );
  },
  deleteEod: (id) =>
    withLocalEodFallback(
      () => reqWithFallback(
        [`/settings/eod/${id}`, `/eod/${id}`, `/calls/eod/${id}`],
        { method: 'DELETE' }
      ),
      () => deleteLocalEodReport(id)
    ),
  getEodTeam: (date) => {
    const qs = '?' + new URLSearchParams(date ? { date } : {});
    return reqWithFallback([`/settings/eod/team${qs}`, `/eod/team${qs}`, `/calls/eod/team${qs}`]);
  },
  isEodApiAvailable: () => !eodApiSkipped(),
  markEodApiUnavailable: markEodApiUnavailable,

  // Discord daily reports
  getDailyReportStatus: () => req('/settings/discord/status'),
  getDailyReportPreview: (params) => req('/settings/discord/preview?' + new URLSearchParams(params || {})),
  sendDailyReport: (data) => req('/settings/discord/send', { method: 'POST', body: data || {} }),

  // Probable Clients
  getProbableClients: () => req('/probableclients'),
  createProbableClient: (data) => req('/probableclients', { method: 'POST', body: data }),
  updateProbableClient: (id, data) => req('/probableclients/' + id, { method: 'PUT', body: data }),
  deleteProbableClient: (id) => req('/probableclients/' + id, { method: 'DELETE' }),
  addProbableClientRemark: (id, text) => req('/probableclients/' + id + '/remark', { method: 'POST', body: { text } }),
};

export default api;
